import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { view } from "./OncoalertDetailsView";
import { useParams } from "react-router-dom";
import {
  logAnalyticEvent,
  getUrlParams,
  getISOFormattedDateTimeString,
} from "../../utils";
import { useTimeSegmentedTweets } from "../../store/queries/conference";
import {
  useGetTagDols,
  useGetReferredHandlesCount,
  useGetTagDolsByViews,
  useGetTagCountrySegmentedSummary,
  useGetTagOverallStats,
  useTopConferences,
} from "../../store/queries/tags";
import {
  useIngestionTweetCount,
  useIngestionAccountCount,
  useIngestionTweetsPerHour,
} from "../../store/queries/search";
import { useGetTweets } from "../../store/queries/tweets";
import { settingsActions } from "../../store/actions";
import moment from "moment";
export const OncoalertDetails = () => {
  const dispatch = useDispatch();

  const queryParams = getUrlParams(location.search);
  const { name } = useParams();
  const auth = useSelector((state) => state.auth.data);
  const oncoalertSettings = useSelector((state) => state.settings?.oncoalert);

  const [hashtag, setHashtag] = useState({});
  const [maxResult, setMaxResult] = useState(15);
  const [selectedDates, setSelectedDates] = useState({
    start: null,
    end: null,
  });
  const [showSignupModal, setShowSignupModal] = useState(false);

  const getStartDate = (startTime) => {
    return getISOFormattedDateTimeString(
      moment(startTime)?.utc().startOf("day")
    );
  };
  const getEndDate = (endTime) => {
    return getISOFormattedDateTimeString(moment(endTime)?.utc().endOf("day"));
  };

  const { data: conference, isSuccess: conferenceSuccess } = useTopConferences({
    term: `#${name}`,
  });

  const { data: tweets, isLoading: isLoadingTweets } = useGetTweets(
    {
      ...(auth?.id && { userId: auth.id }),
      addTagIds: [hashtag?.tag?.id],
      maxResult: 1,
      pageNum: 0,
      sortBy: "score",
      sortOrder: "desc",
      originalOnly: false,
      includeReplies: true,
      startDate: selectedDates.start,
      endDate: selectedDates.end,
    },
    { enabled: !!hashtag?.tag?.id }
  );

  const { data: ingestionTweetCount } = useIngestionTweetCount({
    searchId: hashtag?.primaryName,
    startDate: selectedDates.start,
    endDate: selectedDates.end,
  });

  const { data: ingestionAccountCount } = useIngestionAccountCount({
    searchId: hashtag?.primaryName,
    startDate: selectedDates.start,
    endDate: selectedDates.end,
  });

  const { data: ingestionTweetsPerHour } = useIngestionTweetsPerHour({
    searchId: hashtag?.primaryName,
    startDate: selectedDates.start,
    endDate: selectedDates.end,
  });

  const { data: influencerStats, isLoading: loadingInfluencerStats } =
    useGetTagOverallStats({
      tagId: hashtag?.tag?.id,
      startDate: selectedDates.start,
      endDate: selectedDates.end,
    });

  const {
    data: prolificTweeters,
    isLoading: isLoadingProlificTweeters,
    isSuccess: isSuccessProlificTweeters,
  } = useGetTagDols({
    tagId: hashtag?.tag?.id,
    startDate: selectedDates.start,
    endDate: selectedDates.end,
    maxResult,
  });

  const {
    data: influentials,
    isLoading: isLoadingInfluentials,
    isSuccess: isSuccessInfluentials,
  } = useGetReferredHandlesCount({
    tagId: hashtag?.tag?.id,
    startDate: selectedDates.start,
    endDate: selectedDates.end,
    maxResult,
  });

  const {
    data: dolsByViews,
    isLoading: isLoadingDolsByViews,
    isSuccess: isSuccessDolsByViews,
  } = useGetTagDolsByViews({
    tagId: hashtag?.tag?.id,
    startDate: selectedDates.start,
    endDate: selectedDates.end,
    maxResult,
  });
  // Add impressions calculation
  const dolsByViewsWithImpressions = dolsByViews?.data
    ?.map((dol) => ({
      ...dol,
      impressions: dol.mentions * dol.numFollowers,
    }))
    ?.sort((a, b) => b.impressions - a.impressions);

  const { data: conferenceByCountry, isLoading: loadingConferenceByCountry } =
    useGetTagCountrySegmentedSummary({
      tagId: hashtag?.tag?.id,
      startDate: selectedDates.start,
      endDate: selectedDates.end,
    });

  const getDateOnly = (dateStr) => dateStr && dateStr.split("T")[0];
  const sameDate =
    getDateOnly(selectedDates.start) === getDateOnly(selectedDates.end);

  const { data: timeSegmentedTweets, isLoading: loadingTimeSegmentedTweets } =
    useTimeSegmentedTweets({
      tagId: hashtag?.tag?.id,
      // parentTagId: hashtag?.pare ntTagId,
      startDate: selectedDates.start,
      endDate: selectedDates.end,
      originalOnly: false,
      includeReplies: false,
      segment: sameDate ? "HOUR" : "DAY",
    });

  const formatTimeData = (data) => {
    if (!data) return [];

    return data.map((item) => {
      return {
        ...(!sameDate && { date: moment(item.timesegment).format("MM/DD") }),
        ...(sameDate && {
          hour: item.timesegment.split(" ")[1].replace("-", ":"),
        }),
        value: item.count,
      };
    });
  };

  useEffect(() => {
    scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (conference?.data?.length > 0) {
      setHashtag(conference?.data?.[0]);
      setSelectedDates(getConferenceMonitorDates());
    }
  }, [conference]);

  useEffect(() => {
    if (conference?.data) {
      logAnalyticEvent(
        "OncoAlert Hashtags",
        {
          page: "Conference hashtag",
          name: conference?.data?.primaryName,
        },
        auth?.id || queryParams?.u || null,
        auth?.displayName || queryParams?.n || null,
        auth?.email || queryParams?.e || null,
        auth?.createdAt || null,
        auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
          ?.preferenceStringValue ||
          queryParams?.t ||
          "MEDICAL",
        null
      );
    }
  }, [conference?.data]);

  const handleDateChange = (dates) => {
    setSelectedDates({
      start: getISOFormattedDateTimeString(dates.startDate),
      end: getISOFormattedDateTimeString(dates.endDate),
    });
  };

  const getTop6Countries = (data) => {
    if (!data) return [];

    const sortedData = [...data].sort((a, b) => b.value - a.value);

    let top6 = sortedData
      .filter((item) => item.country.toLowerCase() !== "undetermined")
      .slice(0, 6);
    // Calculate sum of remaining values plus undetermined
    const otherSum = sortedData.reduce((sum, item, index) => {
      if (index >= 6 || item.country.toLowerCase() === "undetermined") {
        return sum + item.count;
      }
      return sum;
    }, 0);

    // Add "Other" category if there are remaining values
    if (otherSum > 0) {
      top6.push({
        country: "Other",
        count: otherSum,
        countrycode: "xx",
      });
    }

    return top6;
  };

  const conferenceTop6ByCountry = getTop6Countries(conferenceByCountry?.data);

  const handleSignUpClick = () => {
    setShowSignupModal(true);
  };

  const handlePreviousPage = () => {
    const currentPage = window.location.pathname;
    dispatch(
      settingsActions.set({
        oncoalert: { ...oncoalertSettings, previousPage: currentPage },
      })
    );
  };

  const getConferenceMonitorDates = () => {
    const today = moment().utc().endOf("day");
    const monitorStartTime = moment(conference?.data?.[0]?.monitorStartTime)
      .utc()
      .startOf("day");
    const monitorEndTime = moment(conference?.data?.[0]?.monitorEndTime)
      .utc()
      .endOf("day");

    // Case 1: If end date has passed
    if (monitorEndTime.isBefore(today)) {
      return {
        start: getISOFormattedDateTimeString(monitorStartTime),
        end: getISOFormattedDateTimeString(monitorEndTime),
      };
    }

    // Case 2: If initial date has passed
    // if (monitorStartTime.isBefore(today)) {
    //   return {
    //     start: getISOFormattedDateTimeString(monitorStartTime),
    //     end: getISOFormattedDateTimeString(today),
    //   };
    // }

    // Case 3: If initial date is in the future
    return {
      start: getISOFormattedDateTimeString(
        today.clone().subtract(30, "days").startOf("day")
      ),
      end: getISOFormattedDateTimeString(today.endOf("day")),
    };
  };

  return view({
    handleSignUpClick,
    tweet: tweets?.data?.[0],
    isLoadingTweets,
    influentials: {
      data: influentials?.data,
      isLoading: isLoadingInfluentials,
      isSuccess: isSuccessInfluentials,
    },
    prolificTweeters: {
      data: prolificTweeters?.data,
      isLoading: isLoadingProlificTweeters,
      isSuccess: isSuccessProlificTweeters,
    },
    highestImpressions: {
      data: dolsByViewsWithImpressions,
      isLoading: isLoadingDolsByViews,
      isSuccess: isSuccessDolsByViews,
    },
    conference: conference?.data,
    conferenceSuccess,
    conferenceByCountry: conferenceTop6ByCountry,
    loadingConferenceByCountry,
    influencerStats: influencerStats?.data,
    loadingInfluencerStats,
    timeSegmentedTweets: formatTimeData(timeSegmentedTweets?.data),
    loadingTimeSegmentedTweets,
    conferenceDates: {
      start: getStartDate(conference?.data?.[0]?.startDate),
      end: getEndDate(conference?.data?.[0]?.endDate),
    },
    conferenceMonitorDates: getConferenceMonitorDates(),
    handleDateChange,
    selectedDates,
    showSignupModal,
    setShowSignupModal,
    maxResult,
    setMaxResult,
    handlePreviousPage,
    ingestionTweetCount: ingestionTweetCount?.data || 0,
    ingestionAccountCount: ingestionAccountCount?.data || 0,
    ingestionTweetsPerHour: ingestionTweetsPerHour?.data || 0,
    sameDate,
  });
};
